import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sunset, Linkedin, Download, Globe, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Lock body scroll when menu is open
  React.useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Hide main content when menu is open
      const main = document.querySelector('main') || document.querySelector('#root > div > div');
      if (main && main.parentElement) {
        main.parentElement.style.position = 'relative';
      }
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <>
    <header className={`fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-200 ${mobileMenuOpen ? 'z-[100]' : 'z-50'}`}>
      <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 group" onClick={closeMobileMenu}>
          <Sunset className="w-6 h-6 text-teal-600 group-hover:rotate-180 transition-transform duration-500" />
          <span className="text-xl font-bold text-slate-900">Joana Pestana Ramos</span>
        </Link>
        
        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-slate-700 hover:text-teal-600 transition-colors"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <a href="/#portfolio" className="text-slate-600 hover:text-teal-600 transition-colors font-medium">
            {t.nav.portfolio}
          </a>
          <a href="/#about" className="text-slate-600 hover:text-teal-600 transition-colors font-medium">
            {t.nav.about}
          </a>
          <a 
            href="https://www.linkedin.com/in/joanapr" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors font-medium"
          >
            <Linkedin className="w-5 h-5" />
            {t.nav.linkedin}
          </a>
          
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors font-medium px-3 py-2 rounded-lg border-2 border-slate-200 hover:border-teal-600"
            aria-label="Toggle language"
          >
            <Globe className="w-4 h-4" />
            <span className="text-sm font-bold">{language === 'en' ? 'FR' : 'EN'}</span>
          </button>
          
          <a 
            href="/cv/Joana_Pestana_Ramos_CV.pdf" 
            download="Joana_Pestana_Ramos_CV.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors font-medium border-2 border-teal-600 hover:border-teal-700 px-4 py-2 rounded-lg"
          >
            <Download className="w-4 h-4" />
            {t.nav.downloadCV}
          </a>
          <a href="/#contact" className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium">
            {t.nav.contact}
          </a>
        </nav>

        {/* Mobile Navigation Menu - Slide-in Drawer */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="md:hidden fixed inset-0 bg-black/50 z-[90]"
              style={{ top: '73px' }}
              onClick={closeMobileMenu}
            ></div>
            
            {/* Drawer - Full width white panel */}
            <div 
              className="md:hidden fixed inset-x-0 top-0 bg-white z-[100] shadow-xl overflow-y-auto"
              style={{ top: '73px', maxHeight: 'calc(100vh - 73px)' }}
            >
              <nav className="flex flex-col p-8 space-y-3">
                <a 
                  href="/#portfolio" 
                  onClick={closeMobileMenu}
                  className="text-slate-900 hover:bg-teal-50 hover:text-teal-600 transition-colors font-semibold py-4 px-4 rounded-lg text-lg border-b border-slate-100"
                >
                  📁 {t.nav.portfolio}
                </a>
                <a 
                  href="/#about" 
                  onClick={closeMobileMenu}
                  className="text-slate-900 hover:bg-teal-50 hover:text-teal-600 transition-colors font-semibold py-4 px-4 rounded-lg text-lg border-b border-slate-100"
                >
                  👤 {t.nav.about}
                </a>
                <a 
                  href="https://www.linkedin.com/in/joanapr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 text-slate-900 hover:bg-teal-50 hover:text-teal-600 transition-colors font-semibold py-4 px-4 rounded-lg text-lg border-b border-slate-100"
                >
                  <Linkedin className="w-6 h-6" />
                  {t.nav.linkedin}
                </a>
                
                {/* Language Toggle */}
                <button
                  onClick={() => {
                    toggleLanguage();
                    closeMobileMenu();
                  }}
                  className="flex items-center gap-3 text-slate-900 hover:bg-teal-50 hover:text-teal-600 transition-colors font-semibold py-4 px-4 rounded-lg text-left text-lg w-full border-b border-slate-100"
                >
                  <Globe className="w-6 h-6" />
                  <span>{language === 'en' ? '🇫🇷 Français' : '🇬🇧 English'}</span>
                </button>
                
                <a 
                  href="/cv/Joana_Pestana_Ramos_CV.pdf" 
                  download="Joana_Pestana_Ramos_CV.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 text-teal-600 hover:bg-teal-50 transition-colors font-semibold py-4 px-4 rounded-lg text-lg border-2 border-teal-200 bg-teal-50/50"
                >
                  <Download className="w-6 h-6" />
                  {t.nav.downloadCV}
                </a>
                
                <a 
                  href="/#contact" 
                  onClick={closeMobileMenu}
                  className="bg-teal-600 text-white px-6 py-4 rounded-lg hover:bg-teal-700 transition-colors font-semibold text-center text-lg shadow-lg mt-2"
                >
                  ✉️ {t.nav.contact}
                </a>
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;